import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AuthModal from './AuthModal';
import { useAuth } from '../../hooks/useAuth';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const { user, loading, signOut } = useAuth();

  const handleAuthSuccess = () => {
    window.location.href = '/dashboard';
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
    window.location.href = '/';
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <i className="ri-brain-line text-white text-xl"></i>
              </div>
              <span
                className={`text-2xl font-bold transition-colors ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
                style={{ fontFamily: '"Brush Script MT", cursive' }}
              >
                Mindshift AI
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-200'
                }`}
              >
                Home
              </Link>
              <Link
                to="/so-funktioniert-es"
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-200'
                }`}
              >
                So funktioniert's
              </Link>
              <Link
                to="/kontakt"
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  isScrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-200'
                }`}
              >
                Kontakt
              </Link>

              <a
                href="/#partnership"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold cursor-pointer whitespace-nowrap transition-all duration-300 animate-pulse hover:animate-none ${
                  isScrolled
                    ? 'border-amber-500 bg-amber-50 text-amber-600 hover:bg-amber-100'
                    : 'border-amber-400 bg-amber-400/10 text-amber-300 hover:bg-amber-400/25 backdrop-blur-sm'
                }`}
                style={{ animationDuration: '2.5s' }}
              >
                <i className="ri-handshake-line text-sm"></i>
                Kooperationen erwünscht
              </a>

              {!loading && (
                <>
                  {user ? (
                    <div className="relative">
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-user-line"></i>
                        <span className="text-sm font-medium">{user.displayName}</span>
                        <i className={`ri-arrow-down-s-line transition-transform ${showUserMenu ? 'rotate-180' : ''}`}></i>
                      </button>

                      {showUserMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                          <Link
                            to="/dashboard"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => setShowUserMenu(false)}
                          >
                            <i className="ri-dashboard-line mr-2"></i>
                            Dashboard
                          </Link>
                          <Link
                            to="/dashboard/einstellungen"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => setShowUserMenu(false)}
                          >
                            <i className="ri-settings-3-line mr-2"></i>
                            Einstellungen
                          </Link>
                          <hr className="my-2 border-gray-200" />
                          <button
                            onClick={handleSignOut}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-logout-box-line mr-2"></i>
                            Abmelden
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsAuthModalOpen(true)}
                      className="px-6 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Kostenlos starten
                    </button>
                  )}
                </>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 cursor-pointer"
            >
              <i
                className={`text-2xl transition-colors ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                } ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <nav className="px-4 py-4 space-y-3">
              <Link
                to="/"
                className="block text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/so-funktioniert-es"
                className="block text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                So funktioniert's
              </Link>
              <Link
                to="/kontakt"
                className="block text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Kontakt
              </Link>
              <a
                href="/#partnership"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-1.5 w-fit px-3 py-1.5 rounded-full border border-amber-500 bg-amber-50 text-amber-600 text-xs font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-handshake-line text-sm"></i>
                Kooperationen erwünscht
              </a>

              {!loading && (
                <>
                  {user ? (
                    <>
                      <Link
                        to="/dashboard"
                        className="block text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <i className="ri-dashboard-line mr-2"></i>
                        Dashboard
                      </Link>
                      <button
                        onClick={() => {
                          handleSignOut();
                          setIsMobileMenuOpen(false);
                        }}
                        className="block w-full text-left text-red-600 hover:text-red-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-logout-box-line mr-2"></i>
                        Abmelden
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setIsAuthModalOpen(true);
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full px-6 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Kostenlos starten
                    </button>
                  )}
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
}
